import { Button } from "@provenleap/ui";
import type { ComponentType } from "react";
import { SetupSteps } from "./SetupSteps";
import type { ToastType } from "../../hooks/useToast";

export interface Assistant {
  name: string;
  icon: ComponentType<{ size?: number; className?: string }>;
  description: string;
  connectUrl: string;
  setupSteps: string[];
}

interface AIAssistantCardProps {
  assistant: Assistant;
  mcpServerUrl: string;
  disabled?: boolean;
  showToast: (type: ToastType, message: string) => void;
}

export function AIAssistantCard({
  assistant,
  mcpServerUrl,
  disabled,
  showToast,
}: AIAssistantCardProps) {
  const ItemIcon = assistant.icon;
  const idPrefix = assistant.name.toLowerCase().replace(/\s+/g, "-");

  async function handleConnect() {
    try {
      await navigator.clipboard.writeText(mcpServerUrl);
      showToast(
        "success",
        `URL copied — ${assistant.name} is opening in a new tab. Click 'Add custom connector' and paste the URL.`,
      );
      window.open(assistant.connectUrl, "_blank", "noopener,noreferrer");
    } catch {
      showToast("error", "Couldn't copy URL — please copy manually");
    }
  }

  return (
    <div
      className="rounded-[22px] bg-background p-6 shadow-xs hover:shadow-sm transition-shadow flex flex-col"
      style={{ border: "0.5px solid var(--hairline)" }}
      data-testid={`assistant-card-${idPrefix}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex items-center justify-center w-10 h-10 shrink-0">
          <ItemIcon size={26} className="text-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-semibold text-foreground">
            {assistant.name}
          </h3>
          <p className="text-[13px] text-foreground mt-0.5">
            {assistant.description}
          </p>
        </div>
      </div>

      <div className="mt-5">
        <Button
          variant="brand"
          className="w-full rounded-2xl text-[13px] cursor-pointer"
          onClick={handleConnect}
          disabled={disabled}
          data-testid={`connect-${idPrefix}`}
        >
          Connect to {assistant.name}
        </Button>
      </div>

      <SetupSteps steps={assistant.setupSteps} idPrefix={idPrefix} />
    </div>
  );
}
